import { Solver, Variable, Expression, Constraint, Strength, Operator } from 'kiwi.js';
import S from 's-js';
import { AssertExtends } from 'lib/assertExtends';
import TZDate from 'lib/TZDate';
import { TZUtils } from 'lib/tzutils';
import { PortCall } from 'data/models/PortCall';
import { ScheduleDateEntity as DateVar } from 'data/models/ScheduleDateEntity';
import { ScheduleDateEvent as DateEvent } from 'data/models/ScheduleDateEvent';
import { ScheduleDateEventType as EventType } from 'data/models/ScheduleDateEventType';
import { PortCallChange } from 'data/models/PortCallChange';
import { PortCallChangeType } from 'server/enums/PortCallChangeType';

export { Rel as ScheduleRel };
export enum Rel {
    Eq = "eq",
    LEq = "leq"
}

export { Rule as ScheduleRule };
class Rule { // like "a = b - offset" or "a <= b - offset"
    constructor(
        public a: DateVar,
        public op: Rel,
        public b: DateVar | TZDate,
        public offset = 0
    ) {}

    /**
     * Is this rule satisfied? Intended for consumers who want to query this rule outside of the context of a
     * `ScheduleChangeEngine` instance.
     */
    isSatisfied() {
        const
            a = +this.a.Estimated() + this.offset,
            b = this.b instanceof TZDate ? +this.b : +this.b.Estimated(),
            op = this.op;
        return (
            op === Rel.Eq  ? Math.abs(a - b) < DEFAULT_DATE_PRECISION :
            op === Rel.LEq ? a < b + DEFAULT_DATE_PRECISION :
            null as AssertExtends<typeof op, never>
        );
    }

    static isRule(rule : unknown) : rule is Rule {
        return rule instanceof Rule;
    }
}

export { Goal as ScheduleGoal };
class Goal {
    rule : Rule;
    constructor(
        public weight : number,
        a : DateVar,
        op : Rel, 
        b : DateVar | TZDate,
        offset = 0
    ) {
        this.rule = new Rule(a, op, b, offset);
    }
}

export { Rules as ScheduleRules };
class Rules {
    public requirements = [] as Rule[];
    public goals = [] as Goal[];
    public defaults = [] as Rule[];
}

export { Policy as SchedulePolicy };
export type Policy = (portcall : PortCall) => (Rule | false | null | undefined)[];

const 
    DEFAULT = Strength.weak,
    ENTRY = Strength.medium,
    RULE = Strength.strong;

export interface ScheduleChangeEngineParams {
    PortCall : PortCall;
    Policy : Policy;
}

export const DEFAULT_DATE_PRECISION = 60 * 1000; // 1 minute increments

export type ScheduleChangeEngine = ReturnType<typeof ScheduleChangeEngine>;
export function ScheduleChangeEngine(tzutils : TZUtils, { PortCall, Policy } : ScheduleChangeEngineParams) {
    const 
        ScheduleChange = new PortCallChange({ Id: 0, Type: PortCallChangeType.Schedule}),
        events = ScheduleChange.ScheduleEvents,
        eventIndex = S(() => events().reduce((idx, e) => (idx[eventKey(e)] = e, idx), {} as { [key : string] : DateEvent | undefined })),
        rules = Policy(PortCall).filter(Rule.isRule);

    ScheduleChange.PortCallId(PortCall.Id());
    ScheduleChange.PortCall(PortCall);
    
    const 
        solver = new Solver(),
        dates = PortCall.Schedule(),
        dateVars = [] as Variable[],
        date2var = new Map<DateVar, Variable>(),
        var2date = new Map<Variable, DateVar>(),
        varStrength = new Map<Variable, number>(),
        // I could MAYBE imagine making datePrecision a configurable param, but for now just setting it to the
        // default value.
        datePrecision = DEFAULT_DATE_PRECISION,
        dateBase = Date.UTC(2010, 0, 1) / datePrecision,
        date2num = (d : TZDate) => Math.round(+d / datePrecision - dateBase),
        num2date = (n : number) => tzutils.newDate(Math.round(n + dateBase) * datePrecision);

    for (let date of dates) {
        const
            dateVar = new Variable(),
            strength = DEFAULT;
        dateVars.push(dateVar);
        date2var.set(date, dateVar);
        var2date.set(dateVar, date);
        varStrength.set(dateVar, strength);

        // All dates default to their current estimated value, even if they have an actual value too. The idea is
        // that our solver operates on the estimated schedule ONLY. Estimates never alter actuals of course, and
        // actuals never cascade into estimates either. While it might sound nice to fine-tune the remaining
        // estimates as actuals come in, in practice that ends up leading to a lot of small tweaks to the
        // estimated schedule once the vessel actually arrives, which isn't desirable. In general, the expectation
        // is that if the vessel is gonna be well ahead of or behind schedule, either the agent or the berth mgr
        // should be using Move PortCall ahead of time to reflect that.
        solver.addEditVariable(dateVar, strength);
        solver.suggestValue(dateVar, date2num(date.Estimated()));
    }

    for (let rule of rules) {
        const 
            a = date2var.get(rule.a)!,
            b = rule.b instanceof TZDate ? date2num(rule.b) : date2var.get(rule.b)!,
            op = rule.op === Rel.Eq ? Operator.Eq : Operator.Le;

        solver.addConstraint(new Constraint(a.plus(rule.offset / datePrecision), op, b, RULE));
    }

    S(() => {
        const
            entries = events().filter(isEnteredEvent),
            changes = events().filter(isChangedEvent);

        // In theory, calling code might specify more than one ...Entered event for a given date entity. We
        // won't make any effort to support two ...Entered events of the SAME TYPE; in practice, the logic below
        // does last one wins for those, which is fine by us. But there is one case we DO wanna support, which
        // is if calling code provides both an ActualDateEntered event and an EstimatedDateEntered event for
        // the same date entity AND the ActualDateEntered event is a clear actual (has Date() === null). That
        // allows calling code to both clear the actual and suggest a new estimate all at the same time. To
        // handle that, we'll input ...Entered events into our solver in a fixed order: First, clear actuals,
        // then estimates, then log/update actuals.
        const entryOrder = (entry : DateEvent) => isActualEnteredEvent(entry) ? entry.Date() === null ? 0 : 2 : 1;
        entries.sort((entry1, entry2) => entryOrder(entry1) - entryOrder(entry2));

        // first suggest values to solver for all entered dates
        for (let entry of entries) {
            const
                date = entry.ScheduleDate()!,
                dateVar = date2var.get(date)!,
                isLogOrEditActual = isActualEnteredEvent(entry) && entry.Date() !== null,
                // Tell our solver to treat the provided entry with a strength in between DEFAULT (mere
                // suggestions that the solver can disregard as needed) and RULE (rules we need the solver to
                // honor always). Unless this is a log/update actual -- in that case, we just wanna UNDO any
                // previous entered estimates we might've already inputted into our solver for this date.
                strength = isLogOrEditActual ? DEFAULT : ENTRY,
                // If this is an entered estimate, then the value we input into our solver is just the entered
                // date. Otherwise, if this is an entered actual, we wanna input the current ESTIMATE into our
                // solver. For clear actuals, that makes sense -- we're defaulting back to the estimated value,
                // and if calling code is ALSO proposing a new estimate, we'll handle that when we process
                // estimates next. For log/update actuals, the idea is to avoid cascading the actual value into
                // other estimated dates, and also to ignore any estimates calling code might've entered for
                // this same date (the entered actual renders any entered estimates irrelevant).
                value = isActualEnteredEvent(entry) ? date.Estimated() : entry.Date()!;

            if (varStrength.get(dateVar) !== strength) {
                solver.removeEditVariable(dateVar);
                solver.addEditVariable(dateVar, strength);
                varStrength.set(dateVar, strength);
            }

            solver.suggestValue(dateVar, date2num(value));
        }

        // run solver
        solver.updateVariables();

        // now record all changes, keeping track of them so that we can clear out 
        // any stale changes that are no longer present
        const 
            changedSet = new Set<string>(),
            recordChangeEvent = (date : DateVar, type : EventType, val : TZDate | null) => {
                const changedKey = buildEventKey(date.Id(), type);

                let change = eventIndex()[changedKey] || null;

                if (change === null) {
                    change = new DateEvent({
                        Id: 0,
                        Type: type,
                        ScheduleDateId: date.Id() 
                    });
                    change.ScheduleChange(ScheduleChange);
                    change.ScheduleDate(date);
                    events.push(change);
                }

                change.Date(val);

                changedSet.add(changedKey);
            };

        const actualEntriesByDate = new Map<DateVar, DateEvent>();
        for (let entry of entries.filter(isActualEnteredEvent)) {
            // Again, we're not trying to support MULTIPLE ActualDateEntered events per date entity, so last one
            // wins is just fine here.
            actualEntriesByDate.set(entry.ScheduleDate()!, entry);
        }

        // Create/update ...Changed events...
        for (let v of dateVars) {
            const
                date = var2date.get(v)!,
                actualEntry = actualEntriesByDate.get(date);

            // Entered actuals, including clears, go directly to ActualDateChanged events, no matter what the
            // solver found. This makes good on our guarantee that we'll respect entered actuals no matter
            // what the estimated schedule is telling us.
            if (actualEntry) recordChangeEvent(date, EventType.ActualDateChanged, actualEntry.Date());

            // In addition, if the resulting state is that there's no actual for this date, AND the solver found
            // a value for this date that differs from the current estimate, then turn that into an
            // EstimatedDateChanged event.
            const newActual = actualEntry ? actualEntry.Date() : date.Actual();
            if (newActual === null && date2num(date.Estimated()) !== v.value()) {
                recordChangeEvent(date, EventType.EstimatedDateChanged, num2date(v.value()));
            }
        }

        // remove stale ...Changed events
        for (let change of changes) {
            if (!changedSet.has(eventKey(change))) {
                events.remove(change);
            }
        }
    });

    // testing engine which just assumes all entries were atomic and OK
    //S(() => {
    //    for (let i = 0; i < events().length; i++) {
    //        let event = events()[i],
    //            match = eventIndex()[matchingChangeEventKey(event)] || null;
    //        if (isEnteredEvent(event)) {
    //            if (match === null) {
    //                match = new DateEvent({ Id: 0, 
    //                    Type: matchingEventType[event.Type()!], 
    //                    ScheduleDateId: event.ScheduleDateId() 
    //                });
    //                match.ScheduleChange(ScheduleChange);
    //                match.ScheduleDate(event.ScheduleDate());
    //                events.push(match);
    //            }
    //            match.Date(event.Date());
    //        } else {
    //            if (match === null) {
    //                events.remove(event);
    //            }
    //        }
    //    }
    //});

    return { ScheduleChange };
}

const 
    isEnteredEvent = (sde : DateEvent) =>
        sde.Type() === EventType.EstimatedDateEntered ||
        sde.Type() === EventType.ActualDateEntered,
    isChangedEvent = (sde : DateEvent) =>
        sde.Type() === EventType.EstimatedDateChanged ||
        sde.Type() === EventType.ActualDateChanged,
    isActualEnteredEvent = (sde : DateEvent) =>
        sde.Type() === EventType.ActualDateEntered,
    buildEventKey = (id : number, type : EventType) => `${id}:${type}`,
    eventKey = (sde : DateEvent) => buildEventKey(sde.ScheduleDateId(), sde.Type() as EventType),
    matchingEventType = {
        [EventType.EstimatedDateEntered]: EventType.EstimatedDateChanged,
        [EventType.EstimatedDateChanged]: EventType.EstimatedDateEntered,
        [EventType.ActualDateEntered]: EventType.ActualDateChanged,
        [EventType.ActualDateChanged]: EventType.ActualDateEntered
    },
    matchingChangeEventKey = (sde : DateEvent) =>
        buildEventKey(sde.ScheduleDateId(), matchingEventType[sde.Type() as EventType]);

export { eventKey, buildEventKey };

/*

type Pred = (a : DateVar, i : number, dates : DateVar[]) => boolean;
type PairPred = (a : DateVar, b : DateVar, ai : number, bi : number, dates : DateVar[]) => boolean;
type Pattern = (pc : PortCall) => Rule[];

type DateReducer<Acc> = Reducer<Acc, [DateVar, number, DateVar[]]>;
type PairedDateReducer = Reducer<Rule[], [DateVar, DateVar, number, number, DateVar[]]>;
type DateTransducer<Acc> = Transducer<Acc, [DateVar, number, DateVar[]], [DateVar, number, DateVar[]]>;
type DateJoinTransducer = Transducer<Rule[], [DateVar, DateVar, number, number, DateVar[], DateVar[]], [DateVar, number, DateVar[]]>;
type PairedDateTransducer = Transducer<Rule[], [DateVar, DateVar, number, number, DateVar[]], [DateVar, DateVar, number, number, DateVar[]]>;
type PatternTransducer = Transducer<Rule[], [Rule], [DateVar, number, DateVar[]]>;

const
    join3 = (filter : DateTransducer<DateVar[]>) : DateJoinTransducer => {
        let filtered = null as null | DateVar[];
        return joinArray<Rule[], DateVar, DateVar>((a, ai, as) => 
            filtered || (filtered = as.reduce(filter(collect), [] as DateVar[]))
        );
    },
    join2 = (tx : DateTransducer) : DateJoinTransducer => 
        selectMany((a, ai, dates) => (step, acc) => dates.reduce(tx((acc, b, bi) => step(acc, a, b, ai, bi, dates)), acc)),
    join = (tx : DateTransducer) =>
        (step : PairedDateReducer) : DateReducer<Rule[]> => 
            (acc, a, ai, dates) => 
                dates.reduce(tx((acc, b, bi) => step(acc, a, b, ai, bi, dates)), acc),
    isType = (t : SDType) : Pred => a => a.Type() === t,
    isIPArrival = isType(SDType.IPArrival),
    isIPDeparture = isType(SDType.IPDeparture),
    isPJOStart = isType(SDType.PJOStart),
    isPJOEnd = isType(SDType.PJOEnd),
    and = <T extends any[]>(...preds : ((...args: T) => boolean)[]) : (...args : T) => boolean => (...args) => preds.reduce((r, p) => r && p(...args), true),
    or  = <T extends any[]>(...preds : ((...args: T) => boolean)[]) : (...args : T) => boolean => (...args) => preds.reduce((r, p) => r || p(...args), false),
    fst = (...preds : Pred[]) : PairPred => (a, b, ai, bi, dates) => and(...preds)(a, ai, dates),
    snd = (...preds : Pred[]) : PairPred => (a, b, ai, bi, dates) => and(...preds)(b, bi, dates),
    allDatePairsWhere = (fst : Pred | null, snd : Pred | null, combo : PairPred | null, rule : (fst : DateVar, snd : DateVar) => Rule) => (dates : DateVar[]) => {
        const left = fst ? dates.filter(fst) : dates,
            right = snd ? dates.filter(snd) : dates,
            pairs = left.map((a, ai) => 
                (combo ? right.filter((b, bi) => combo(a, b, ai, bi, dates)) : right)
                    .map(((b, bi) => rule(a, b))));
            return pairs.reduce((a, b) => a.concat(b), []);
    },
    datePJO = (pjoDate : DateVar) => pjoDate.JobOrder() && pjoDate.JobOrder()!.PilotJobOrder();

//const allDates = (tx : PatternTransducer) => (pc : PortCall) => pc.Schedule().reduce(tx((rs, r) => (rs.push(r), rs)), []);

var sync : PatternTransducer = 
    compose(
        where(isIPDeparture),
        join3(where(isPJOStart)),
        where((ip, pjo) => ip.ItineraryPointId() === datePJO(pjo)!.FromPointId()),
        select((a, b) => new Rule(a, Op.Eq, b))
    );

const foo = compose(
    x => x() + 1,
    compose
);

const addRequirements : (...p : Parameters<typeof compose>) => Rule[] = compose(
    (tx : DateTransducer<Rule[]>) : Pattern => (pc : PortCall) => into([] as Rule[], tx, pc.Schedule()),
    compose
)

var addGoal: Pattern, 
    allDates! : (pc : PortCall) => DateVar[],
    hasEntry! : (d : DateVar) => boolean, 
    CurrentBestEntry : (d : DateVar) => DateEvent,
    ConcurentTime : (d : DateVar, e : DateEvent) => DateEvent,
    DateDiff : (a : DateEvent, b: DateEvent) => number,
    EntryTime : (e : DateEvent) => number,
    ItineraryPoint : (d : DateVar) => ItineraryPoint,
    ItineraryArrival : (ip : ItineraryPoint) => DateVar;

var anchors = 
    addGoal(
        select(allDates),
        where(and(isIPDeparture, hasEntry)),
        join((d) => ItineraryArrival(ItineraryPoint(d))),
        join((d, a) => CurrentBestEntry(d)),
        join((d, a, dt) => ConcurentTime(a, dt)),
        select((d, a, dt, at) => new Goal(EntryTime(at), a, Op.LEq, d, DateDiff(dt, at)))
    );

{
    const select1 = allDates,
        where2 = (d : DateVar) => and(isIPDeparture, hasEntry)(d, 0, []),
        join3 = (d : DateVar) => ItineraryArrival(ItineraryPoint(d)),
        join4 = (d : DateVar, a : DateVar) => CurrentBestEntry(d),
        join5 = (d : DateVar, a : DateVar, dt : DateEvent) => ConcurentTime(a, dt),
        select6 = (d : DateVar, a : DateVar, dt : DateEvent, at : DateEvent) => new Goal(EntryTime(at), a, Op.LEq, d, DateDiff(dt, at));

    const anch = (result, p1) => {
        let p2, p3, p4, p5;
        p1 = select1(p1);
        if (where2(p1)) return;
        p2 = join3(p1);
        p3 = join4(p1, p2);
        p4 = join5(p1, p2, p3);
        p5 = select6(p1, p2, p3, p4);
        result(p5);
    }

    const anchtx = compose(
        ((step : Reducer<Goal[], [DateVar]>)                                => (acc : Goal[], p1 : PortCall) => { for (var p2 of select1(p1)) step(acc, p2); }),
        ((step : Reducer<Goal[], [DateVar]>)                                => (acc : Goal[], p1 : DateVar) => where2(p1) ? step(acc, p1) : acc),
        ((step : Reducer<Goal[], [DateVar, DateVar]>)                       => (acc : Goal[], p1 : DateVar) => step(acc, p1, join3(p1))),
        ((step : Reducer<Goal[], [DateVar, DateVar, DateEvent]>)            => (acc : Goal[], p1 : DateVar, p2 : DateVar) => step(acc, p1, p2, join4(p1, p2))),
        ((step : Reducer<Goal[], [DateVar, DateVar, DateEvent, DateEvent]>) => (acc : Goal[], p1 : DateVar, p2 : DateVar, p3 : DateEvent) => step(acc, p1, p2, p3, join5(p1, p2, p3))),
        ((step : Reducer<Goal[], [Goal]>)                                   => (acc : Goal[], p1 : DateVar, p2 : DateVar, p3 : DateEvent, p4 : DateEvent) => step(acc, select6(p1, p2, p3, p4)))
    )
}

{
    const select1 = allDates,
        where2 = and(isIPDeparture, hasEntry),
        join3 = (d) => ItineraryArrival(ItineraryPoint(d)),
        join4 = (d, a) => CurrentBestEntry(d),
        join5 = (d, a, dt) => ConcurentTime(a, dt),
        select6 = (d, a, dt, at) => new Goal(EntryTime(at), a, Op.LEq, d, DateDiff(dt, at)),

    const anch = (result, p1) => {
        let p2, p3, p4, p5;
        p1 = select1(p1);
        if (where2(p1)) return;
        p2 = join3(p1);
        p3 = join4(p1, p2);
        p4 = join5(p1, p2, p3);
        p5 = select6(p1, p2, p3, p4);
        result(p5);
    }
}

var dates = [] as DateVar[];
var sel : Transducer<Rule[], [Rule], [DateVar]> = select((a : DateVar) => new Rule(a, Op.Eq, a));
var collect = <T>(acc : T[], x : T) => (acc.push(x), acc);
var lred = (acc : Rule[], x : Rule) => (acc.push(x), acc);
var rules = dates.reduce(sel(lred), []);

var syncPJOStartWithIPDeparture = allDatePairsWhere(
    isIPDeparture,
    isPJOStart,
    null,
    (a, b) => new Rule(a, Op.Eq, b)
);
*/


// WEBPACK FOOTER //
// ./apps/common/services/scheduleChangeEngine.ts